import { useEffect, useState, useId } from 'react';
import useStore from '@/store/useStore';
import { openDEChat } from '@/components/DigitalEngagementChat/DigitalEngagementChat';
import RichText from '@/components/RichText/RichText';
import { ColorKeys } from '@/styles/colors';
import ResponsiveImage from '@/utils/ResponsiveImage';
import beTwMerge from '@/utils/beTwMerge';
import { clearHTMLTags, getAnchorId } from '@/utils/builderUtils';
import DropdownButton from '@/components/homepage/DropdownButton';
import Button from '@/components/homepage/Button';
import useMediaQuery from '@/hooks/useMediaQuery';
import { GaEventsProps } from '@/store/types/IBuilder';
import { getDataAttributes } from '@/utils/dataAttributes';
import { trackEventWithGaEvents } from '@/utils/gaDataPush';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import { useBuilderProvider } from '@/context/BuilderContext';
import { trackTrackOrderContactUsClick } from '@/components/BuilderIO/common/NavarTrackInfo/trackOrderAnalytics';
import { ITitle } from '../CommonFields';

type GenericGaProps = {
  gaEvents?: GaEventsProps[];
};

type Action = GenericGaProps & {
  label: string;
  type?: 'primary' | 'secondary';
  color?: ColorKeys;
  onClick?: () => void;
  href?: string;
  actions?: Action[];
  dropdown?: boolean;
};

interface CustomAction extends Action {
  action: 'openLivePerson' | 'openPromoModal' | 'openEmailUsModal';
  background: 'transparent' | 'solid';
  variant: 'primary' | 'secondary';
}

interface ILinkTileProps {
  background: 'Dark Green' | 'Light Green' | 'Light Mauve' | 'Cream' | 'White';
  sectionHeader: {
    optionalLargeHeader: ITitle;
    optionalDescription: string;
  };
  linkGroups: {
    icon: {
      iconUrl: string;
    };
    label: {
      labelText: string;
      labelTag: 'h1' | 'h2' | 'h3';
    };
    linkDescription?: string;
    isLinkGroupCtaAPopup?: boolean;
    isLinkedGroup: boolean;
    link?: string;
    gaEvents?: GaEventsProps[];
    onClick?: () => void;
    cta: CustomAction;
  }[];
  ctas?: Action[];
  margin?: boolean;
  anchorLinkID?: string;
}

export default function LinkTile({
  background,
  sectionHeader,
  linkGroups,
  ctas,
  margin,
  anchorLinkID,
}: ILinkTileProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [itemWidth, setItemWidth] = useState(null);
  const [ctasWidth, setCtasWidth] = useState(null);
  const randomId = useId().replace(/:/g, '');

  const actions = ctas?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));
  const backgroundColor = {
    'dark-green': 'tw-bg-color-dark-green tw-text-white',
    'light-green': 'tw-bg-color-light-green tw-text-white',
    'lighter-green': 'tw-bg-color-light-green-light tw-text-white',
    'light-mauve': 'tw-bg-color-light-mauve tw-text-color-black',
    cream: 'tw-bg-color-cream tw-text-color-black',
    white: 'tw-bg-white tw-text-color-black',
  };
  const formattedBackground = background?.replace(' ', '-').toLowerCase();
  const setEmailUsModalVisible = useStore(state => state.setEmailUsModalVisible);
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);
  const { orderStatus } = useBuilderProvider();

  const handleOnClickOption = option => {
    switch (option) {
      case 'openLivePerson':
        // Track Order Contact Us: chat_now_click with chat_origin=disruptive
        openDEChat(orderStatus ? 'disruptive' : '');
        break;
      case 'openPromoModal':
        setIsPromoModalVisible(true);
        break;
      case 'openEmailUsModal':
        setEmailUsModalVisible(true);
        break;
      default:
        break;
    }
  };

  /** Fire Track Order Contact Us GA when on /orderstatus, then any Builder-configured gaEvents. */
  const trackLinkGroupClick = (linkGroup: ILinkTileProps['linkGroups'][number]) => {
    if (orderStatus) {
      trackTrackOrderContactUsClick({
        category: orderStatus.category,
        labelText: linkGroup?.label?.labelText,
        phoneCandidates: [linkGroup?.link, linkGroup?.cta?.href, linkGroup?.linkDescription],
      });
    }
    linkGroup?.gaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
  };

  const handleLinkedGroupClick = (linkGroup: ILinkTileProps['linkGroups'][number]) => {
    trackLinkGroupClick(linkGroup);
    handleOnClickOption(linkGroup.onClick);
  };

  const [HeaderTag, setHeaderTag] = useState(sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2');

  useEffect(() => {
    setHeaderTag(sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2');
  }, [sectionHeader]);

  const headerCustomStyles = {
    h1: 'tw-text-garamond-extralarge-mobile desktop:tw-text-garamond-extralarge-desktop tw-font-normal tw-mb-0',
    h2: 'tw-text-garamond-large-mobile desktop:tw-text-garamond-large-desktop tw-font-normal tw-mb-0',
  };
  const subHeadersStyles = {
    h1: 'tw-text-garamond-extralarge-mobile desktop:tw-text-garamond-extralarge-desktop',
    h2: 'tw-text-garamond-large-mobile desktop:tw-text-garamond-large-desktop',
    h3: 'tw-text-garamond-medium-mobile desktop:tw-text-garamond-medium-desktop',
  };

  useEffect(() => {
    const buttons = document.querySelectorAll(`.${randomId}-cta-button`);
    const ctaActions = document.querySelectorAll(`.${randomId}-cta-actions`);

    calculateCtaDimensions(buttons, setItemWidth);
    calculateCtaDimensions(ctaActions, setCtasWidth);
  }, [randomId]);

  const calculateCtaDimensions = (buttons: any, setDimension: any) => {
    if (buttons.length > 0) {
      const dimensions: any[] = [];

      for (let i = 0; i < buttons.length; i += 1) {
        const item = buttons[i];
        const { width } = item.getBoundingClientRect();
        dimensions.push(width);
      }
      dimensions.sort((a, b) => b - a);

      setDimension(dimensions[0]);
    }
  };

  return (
    <div
      className={beTwMerge(
        'tw-mx-4 lg:tw-mx-8 min-[1472px]:tw-w-full min-[1472px]:tw-mx-auto tw-max-w-[1440px]',
        margin ? 'tw-mb-20' : ''
      )}
      {...getAnchorId(anchorLinkID)}
    >
      <div
        className={beTwMerge(
          'tw-py-10 lg:tw-py-14 tw-grid tw-col-span-full tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12 tw-gap-x-4 lg:tw-gap-x-6',
          backgroundColor[formattedBackground],
          formattedBackground === 'white' ? 'tw-py-0 md:tw-py-0 lg:tw-py-0' : ''
        )}
      >
        {/* Section Header */}
        {(!!sectionHeader?.optionalLargeHeader?.content || !!sectionHeader?.optionalDescription) && (
          <div
            className={beTwMerge(
              'tw-px-2 md:tw-px-6 tw-col-span-full flex flex-col items-center lg:tw-max-w-[1172px] tw-mx-auto',
              // eslint-disable-next-line no-nested-ternary
              !linkGroups?.length ? 'tw-mb-0' : sectionHeader?.optionalDescription ? 'tw-mb-10' : 'tw-mb-7'
            )}
          >
            {!!sectionHeader?.optionalLargeHeader?.content && (
              <HeaderTag
                className={beTwMerge(
                  'tw-text-center',
                  headerCustomStyles[sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2'],
                  sectionHeader?.optionalDescription && 'tw-mb-4'
                )}
              >
                {sectionHeader?.optionalLargeHeader?.content}
              </HeaderTag>
            )}
            {!!sectionHeader?.optionalDescription && (
              <RichText
                className={beTwMerge(
                  'tw-text-center',
                  formattedBackground === 'dark-green' ||
                    formattedBackground === 'light-green' ||
                    formattedBackground === 'lighter-green'
                    ? 'tw-text-color-white'
                    : ''
                )}
                content={sectionHeader.optionalDescription}
              />
            )}
          </div>
        )}

        {/* Link Groups Section */}
        <div
          className={beTwMerge(
            'tw-col-span-full tw-grid tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12 tw-px-4 md:tw-px-6 md:tw-gap-x-6 tw-gap-y-6 lg:tw-gap-y-0',
            ctas?.length && 'tw-mb-10'
          )}
        >
          {linkGroups?.slice(0, 4).map((linkGroup, idx) => {
            const SubHeaderTitle = linkGroup?.label?.labelTag || 'h3';
            return (
              <div
                key={`${linkGroup?.label?.labelText?.replace(' ', '-')}-${idx}`}
                className={beTwMerge(
                  'tw-col-span-full tw-flex tw-flex-col tw-items-center',
                  linkGroups.length % 2 === 0 && 'md:tw-col-span-3',
                  linkGroups.length >= 4 && 'md:tw-col-span-3',
                  linkGroups.length === 3 && 'md:tw-col-span-2 lg:tw-col-span-4',
                  linkGroups.length === 2 && 'lg:tw-col-span-6',
                  linkGroups.length === 1 && 'md:tw-col-span-full',
                  !ctas?.length && idx === linkGroups.length - 1 && 'tw-mb-0'
                )}
              >
                <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-between tw-h-full">
                  <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-group">
                    {linkGroup.icon?.iconUrl &&
                      // eslint-disable-next-line no-nested-ternary
                      (linkGroup.isLinkedGroup ? (
                        <a
                          type="button"
                          className="tw-cursor-pointer focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                          onClick={() => handleLinkedGroupClick(linkGroup)}
                          role="button"
                          aria-label={
                            linkGroup.linkDescription || linkGroup?.label?.labelText === 'Text'
                              ? 'Text Us'
                              : linkGroup?.label?.labelText
                          }
                          tabIndex={0}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              handleLinkedGroupClick(linkGroup);
                            }
                          }}
                        >
                          <ResponsiveImage
                            className="tw-mb-4"
                            sources={[
                              {
                                src: linkGroup?.icon?.iconUrl,
                                size: { width: 48, height: 48, aspectRatio: 1 / 1 },
                                breakpoint: 0,
                              },
                            ]}
                            alt={linkGroup?.label?.labelText === 'Text' ? 'Text Us' : linkGroup?.label?.labelText}
                          />
                        </a>
                      ) : linkGroup.link ? (
                        <a
                          href={linkGroup.link || ''}
                          {...getDataAttributes(linkGroup)}
                          aria-label={
                            linkGroup.linkDescription || linkGroup?.label?.labelText === 'Text'
                              ? 'Text Us'
                              : linkGroup?.label?.labelText
                          }
                          onClick={() => trackLinkGroupClick(linkGroup)}
                        >
                          <ResponsiveImage
                            className="tw-mb-4"
                            sources={[
                              {
                                src: linkGroup?.icon?.iconUrl,
                                size: { width: 48, height: 48, aspectRatio: 1 / 1 },
                                breakpoint: 0,
                              },
                            ]}
                            alt={linkGroup?.label?.labelText === 'Text' ? 'Text Us' : linkGroup?.label?.labelText}
                          />
                        </a>
                      ) : (
                        <ResponsiveImage
                          className="tw-mb-4"
                          sources={[
                            {
                              src: linkGroup?.icon?.iconUrl,
                              size: { width: 48, height: 48, aspectRatio: 1 / 1 },
                              breakpoint: 0,
                            },
                          ]}
                          alt={linkGroup?.label?.labelText === 'Text' ? 'Text Us' : linkGroup?.label?.labelText}
                        />
                      ))}
                    <div className="tw-grid tw-grid-cols-1 tw-items-center tw-content-center tw-jusitify-items-center">
                      <div className={beTwMerge('', !linkGroup.cta?.label ? 'tw-mb-0' : 'tw-mb-4')}>
                        {linkGroup?.label?.labelText &&
                          // eslint-disable-next-line no-nested-ternary
                          (linkGroup.isLinkedGroup ? (
                            <a
                              type="button"
                              className={beTwMerge(
                                'tw-cursor-pointer !tw-no-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]',
                                formattedBackground === 'dark-green' ||
                                  formattedBackground === 'light-green' ||
                                  formattedBackground === 'lighter-green'
                                  ? 'tw-text-color-white'
                                  : ''
                              )}
                              aria-label={
                                linkGroup?.label?.labelText === 'Text' ? 'Text Us' : linkGroup?.label?.labelText
                              }
                              onClick={() => handleLinkedGroupClick(linkGroup)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={event => {
                                if (event.key === 'Enter') {
                                  handleLinkedGroupClick(linkGroup);
                                }
                              }}
                            >
                              <SubHeaderTitle
                                className={beTwMerge(
                                  'tw-text-center tw-mt-0 tw-mb-2 md:tw-mb-4',
                                  !linkGroup?.linkDescription && 'tw-mb-0 md:tw-mb-0',
                                  formattedBackground === 'dark-green' ||
                                    formattedBackground === 'light-green' ||
                                    formattedBackground === 'lighter-green'
                                    ? 'tw-text-color-white'
                                    : 'group-hover:tw-text-be-turquoise-green',
                                  subHeadersStyles[linkGroup.label.labelTag]
                                )}
                              >
                                {linkGroup?.label?.labelText}
                              </SubHeaderTitle>
                            </a>
                          ) : linkGroup.link ? (
                            <SubHeaderTitle
                              className={beTwMerge(
                                'tw-text-center tw-mt-0 tw-mb-2 md:tw-mb-4 tw-text-color-white ',
                                !linkGroup?.linkDescription && 'tw-mb-0 md:tw-mb-0',
                                subHeadersStyles[linkGroup.label.labelTag]
                              )}
                            >
                              <a
                                href={linkGroup.link || ''}
                                className={beTwMerge(
                                  'tw-text-center !tw-no-underline',
                                  formattedBackground === 'dark-green' ||
                                    formattedBackground === 'light-green' ||
                                    formattedBackground === 'lighter-green'
                                    ? 'tw-text-color-white'
                                    : 'tw-text-color-black group-hover:tw-text-be-turquoise-green'
                                )}
                                {...getDataAttributes(linkGroup)}
                                onClick={() => trackLinkGroupClick(linkGroup)}
                                aria-label={
                                  linkGroup?.label?.labelText === 'Text' ? 'Text Us' : linkGroup?.label?.labelText
                                }
                              >
                                {linkGroup?.label?.labelText}
                              </a>
                            </SubHeaderTitle>
                          ) : (
                            <SubHeaderTitle
                              className={beTwMerge(
                                'tw-text-center tw-mt-0 tw-mb-2 md:tw-mb-4',
                                !linkGroup?.linkDescription && 'tw-mb-0 md:tw-mb-0',
                                subHeadersStyles[linkGroup.label.labelTag]
                              )}
                            >
                              {linkGroup?.label?.labelText}
                            </SubHeaderTitle>
                          ))}
                        {linkGroup.linkDescription &&
                          // eslint-disable-next-line no-nested-ternary
                          (linkGroup.isLinkedGroup ? (
                            <a
                              type="button"
                              className="tw-cursor-pointer !tw-no-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                              onClick={() => handleLinkedGroupClick(linkGroup)}
                              aria-label={clearHTMLTags(linkGroup.linkDescription || 'Text Us')}
                              role="button"
                              tabIndex={0}
                              onKeyDown={event => {
                                if (event.key === 'Enter') {
                                  handleLinkedGroupClick(linkGroup);
                                }
                              }}
                            >
                              <RichText
                                className={beTwMerge(
                                  'tw-text-center tw-font-normal tw-text-sm',
                                  linkGroup.cta?.label && 'tw-mb-4',
                                  formattedBackground === 'dark-green' ||
                                    formattedBackground === 'light-green' ||
                                    formattedBackground === 'lighter-green'
                                    ? 'tw-text-color-white'
                                    : ''
                                )}
                                content={linkGroup?.linkDescription}
                              />
                            </a>
                          ) : linkGroup.link ? (
                            <a
                              className="!tw-no-underline"
                              href={linkGroup.link}
                              {...getDataAttributes(linkGroup)}
                              aria-label={clearHTMLTags(linkGroup.linkDescription || 'Text Us')}
                              onClick={() => trackLinkGroupClick(linkGroup)}
                            >
                              <RichText
                                className={beTwMerge(
                                  'tw-text-center tw-font-normal tw-text-sm',
                                  linkGroup.cta?.label && 'tw-mb-4',
                                  formattedBackground === 'dark-green' ||
                                    formattedBackground === 'light-green' ||
                                    formattedBackground === 'lighter-green'
                                    ? 'tw-text-color-white'
                                    : ''
                                )}
                                content={linkGroup.linkDescription}
                              />
                            </a>
                          ) : (
                            <RichText
                              className={beTwMerge(
                                'tw-text-center tw-font-normal tw-text-sm',
                                linkGroup.cta?.label && 'tw-mb-4',
                                formattedBackground === 'dark-green' ||
                                  formattedBackground === 'light-green' ||
                                  formattedBackground === 'lighter-green'
                                  ? 'tw-text-color-white'
                                  : ''
                              )}
                              content={linkGroup.linkDescription}
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                  {linkGroup.cta &&
                    linkGroup.cta?.label &&
                    (linkGroup.cta.actions ? (
                      <DropdownButton
                        label={linkGroup?.cta?.label}
                        options={linkGroup?.cta?.actions}
                        // @ts-ignore
                        color={linkGroup?.cta?.color === 'light' ? 'white' : 'dark-green'}
                        type={linkGroup?.cta?.type}
                        className={beTwMerge(
                          `${randomId}-cta-button tw-basis-[calc(50%-8px)] tw-max-h-[44px] tw-w-full md:tw-w-auto tw-text-center [&>button]:tw-flex-1 [&>button]:tw-w-full [&>button]:md:tw-w-auto [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button]:tw-justify-normal [&>button>span]:tw-grow-[inherit] [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis tw-place-self-center`
                        )}
                        styles={{
                          width: itemWidth && isDesktop ? `${itemWidth}px` : '',
                        }}
                        onToggle={() => {
                          if (orderStatus) {
                            trackTrackOrderContactUsClick({
                              category: orderStatus.category,
                              labelText: linkGroup?.label?.labelText,
                              phoneCandidates: [linkGroup?.link, linkGroup?.cta?.href, linkGroup?.linkDescription],
                            });
                          }
                          linkGroup?.cta?.gaEvents?.forEach(gaEvent => {
                            trackEventWithGaEvents(gaEvent, false);
                          });
                        }}
                      />
                    ) : (
                      <Button
                        className={beTwMerge(
                          // The className "optional-id-me-button" is used for only one button in the website
                          // it's enabled by adding "show-optional-id-me-button" in the css classes of the builder platform
                          `${randomId}-cta-button tw-basis-[calc(50%-8px)] tw-max-h-[44px] tw-text-center tw-whitespace-nowrap tw-place-self-center tw-text-ellipsis tw-overflow-hidden optional-id-me-button`
                        )}
                        label={linkGroup?.cta?.label}
                        // @ts-ignore
                        color={linkGroup?.cta?.color === 'light' ? 'white' : 'dark-green'}
                        type={linkGroup?.cta?.type}
                        href={linkGroup?.cta?.href}
                        ariaLabel={moreDescriptiveAriaLabel(linkGroup?.label?.labelText, linkGroup?.cta?.label)}
                        {...getDataAttributes(linkGroup.cta)}
                        onClick={() => {
                          if (orderStatus) {
                            trackTrackOrderContactUsClick({
                              category: orderStatus.category,
                              labelText: linkGroup?.label?.labelText,
                              phoneCandidates: [linkGroup?.link, linkGroup?.cta?.href, linkGroup?.linkDescription],
                            });
                          }
                          if (!linkGroup.cta.href || linkGroup.isLinkGroupCtaAPopup) {
                            handleOnClickOption(linkGroup.cta.onClick);
                          } else {
                            linkGroup?.cta?.gaEvents?.forEach(gaEvent => {
                              trackEventWithGaEvents(gaEvent, false);
                            });
                          }
                        }}
                        styles={{
                          width: itemWidth && isDesktop ? `${itemWidth}px` : '',
                        }}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Optional CTAs section */}
        <div className="tw-col-span-full tw-px-4 md:tw-px-6 md:tw-flex md:tw-justify-center md:tw-gap-4">
          {actions?.slice(0, 2).map((action, i) => {
            const { label, color, type } = action;

            if (action.actions) {
              const { actions: subActions } = action;
              return (
                <DropdownButton
                  key={i}
                  label={label}
                  options={subActions}
                  // @ts-ignore
                  color={color}
                  type={type}
                  className={beTwMerge(
                    `${randomId}-cta-actions tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`,
                    i !== actions.length - 1 && 'tw-mb-4 md:tw-mb-0'
                  )}
                  styles={{
                    width: ctasWidth && isDesktop ? `${ctasWidth}px` : '',
                  }}
                  onToggle={() => {
                    action?.gaEvents?.forEach(gaEvent => {
                      trackEventWithGaEvents(gaEvent, false);
                    });
                  }}
                />
              );
            }
            const { onClick, href, gaEvents } = action;
            return (
              <Button
                className={beTwMerge(
                  `${randomId}-cta-actions tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`,
                  i !== actions.length - 1 && 'tw-mb-4 md:tw-mb-0'
                )}
                key={i}
                label={label}
                // @ts-ignore
                color={color}
                type={type}
                href={href}
                ariaLabel={moreDescriptiveAriaLabel(sectionHeader?.optionalLargeHeader?.content, label)}
                {...getDataAttributes({ gaEvents })}
                onClick={() => {
                  gaEvents?.forEach(gaEvent => {
                    trackEventWithGaEvents(gaEvent, false);
                  });
                  onClick;
                }}
                styles={{
                  width: ctasWidth && isDesktop ? `${ctasWidth}px` : '',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
